import { useEffect, useState } from "react";
import { LIMITS } from "../constants.js";
import { countAssetsByBrand } from "../lib/generate/countAssetsByBrand.js";
import "./Usage.css";

export default function Usage({ config }) {
  const brands = (config?.config ?? []).filter((item) => item.brand.id !== null);
  const brandCount = brands.length;

  const [assetCounts, setAssetCounts] = useState(null);
  const [assetCountsError, setAssetCountsError] = useState(null);

  useEffect(() => {
    if (brands.length === 0) return;
    let cancelled = false;

    (async () => {
      try {
        const counts = await countAssetsByBrand(
          config,
          brands.map((b) => b.brand.id),
          () => {},
          () => {}
        );
        if (!cancelled) setAssetCounts(counts);
      } catch (err) {
        if (!cancelled) setAssetCountsError(err.message);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brands.length]);

  return (
    <div className="usage-page">
      <h1 className="usage-page__title">Usage</h1>
      <p className="usage-page__subtitle">A snapshot of where this workspace stands against its limits.</p>

      <div className="usage-page__stat-grid">
        <StatCard label="Brands" value={brandCount} cap={LIMITS.MAX_BRANDS_PER_WORKSPACE} />
        <StatCard label="Credits remaining" value={config.credits ?? 0} />
      </div>

      <section className="usage-page__section">
        <h2 className="usage-page__section-title">References per brand</h2>
        <p className="usage-page__section-desc">
          Each brand can hold up to {LIMITS.MAX_REFERENCE_IMAGES_PER_BRAND} reference images (logos,
          products, examples, fonts).
        </p>
        {brands.length === 0 ? (
          <p className="usage-page__empty">No brands yet.</p>
        ) : (
          <ul className="usage-page__breakdown">
            {brands.map((item) => {
              const count = item.references?.length ?? 0;
              return (
                <BreakdownRow
                  key={item.brand.id}
                  name={item.brand.name}
                  value={count}
                  cap={LIMITS.MAX_REFERENCE_IMAGES_PER_BRAND}
                />
              );
            })}
          </ul>
        )}
      </section>

      <section className="usage-page__section">
        <h2 className="usage-page__section-title">Assets per brand</h2>
        <p className="usage-page__section-desc">
          No cap in place yet — this is tracked here as a heads-up ahead of one being added.
        </p>
        {brands.length === 0 ? (
          <p className="usage-page__empty">No brands yet.</p>
        ) : assetCountsError ? (
          <p className="usage-page__empty usage-page__empty--error">{assetCountsError}</p>
        ) : assetCounts === null ? (
          <p className="usage-page__empty">Loading…</p>
        ) : (
          <ul className="usage-page__breakdown">
            {brands.map((item) => (
              <BreakdownRow key={item.brand.id} name={item.brand.name} value={assetCounts[item.brand.id] ?? 0} />
            ))}
          </ul>
        )}
      </section>

      <section className="usage-page__section">
        <h2 className="usage-page__section-title">Storage</h2>
        <p className="usage-page__section-desc usage-page__section-desc--placeholder">
          Coming soon — tracking real storage used across exports and uploaded references needs its
          own dedicated design pass.
        </p>
      </section>
    </div>
  );
}

function StatCard({ label, value, cap }) {
  const pct = cap ? Math.min(100, Math.round((value / cap) * 100)) : null;
  return (
    <div className="usage-page__stat-card">
      <p className="usage-page__stat-label">{label}</p>
      <p className="usage-page__stat-value">
        {value}
        {cap ? <span className="usage-page__stat-cap"> / {cap}</span> : null}
      </p>
      {pct !== null ? (
        <div className="usage-page__track">
          <div
            className={`usage-page__fill${pct >= 100 ? " usage-page__fill--full" : ""}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      ) : null}
    </div>
  );
}

function BreakdownRow({ name, value, cap }) {
  const pct = cap ? Math.min(100, Math.round((value / cap) * 100)) : null;
  return (
    <li className="usage-page__breakdown-row">
      <span className="usage-page__breakdown-name">{name}</span>
      <span className="usage-page__breakdown-value">
        {value}
        {cap ? <span className="usage-page__breakdown-cap"> / {cap}</span> : null}
      </span>
      {pct !== null ? (
        <div className="usage-page__track usage-page__track--row">
          <div
            className={`usage-page__fill${pct >= 100 ? " usage-page__fill--full" : ""}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      ) : null}
    </li>
  );
}